import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  KeyRound,
  Loader2,
  LogOut,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { STATUS_OPTIONS } from "@/constants/site";
import { api, errMessage } from "@/lib/api";

const TOKEN_KEY = "gm_admin_token";

const KINDS = [
  { slug: "leads", label: "Enquiries" },
  { slug: "free-access", label: "Free access" },
  { slug: "purchase-intents", label: "Purchase requests" },
];

const fmtDate = (iso) => {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

export default function AdminPage() {
  const [token, setToken] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [kind, setKind] = useState("leads");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    document.title = "Owner dashboard - Goldmap EA";
    try {
      const saved = window.localStorage.getItem(TOKEN_KEY);
      if (saved) {
        setToken(saved);
        setAuthed(true);
      }
    } catch (e) {
      /* ignore */
    }
  }, []);

  const queryRef = useRef("");
  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  const load = useCallback(
    async (activeToken = token, activeKind = kind) => {
      if (!activeToken) return;
      setLoading(true);
      try {
        const [list, sum] = await Promise.all([
          api.adminList(activeToken, activeKind, {
            q: queryRef.current || undefined,
            status: statusFilter,
          }),
          api.adminSummary(activeToken),
        ]);
        setItems(list.items || []);
        setSummary(sum);
      } catch (error) {
        if (error?.response?.status === 401) {
          toast.error("Session expired. Login again.");
          logout();
        } else {
          toast.error(errMessage(error));
        }
      } finally {
        setLoading(false);
      }
    },
    [token, kind, statusFilter]
  );

  useEffect(() => {
    if (authed) load();
  }, [authed, kind, statusFilter, load]);

  const login = async (e) => {
    e.preventDefault();
    if (!token.trim()) {
      toast.error("Enter the admin token.");
      return;
    }
    setLoggingIn(true);
    try {
      await api.adminLogin(token.trim());
      window.localStorage.setItem(TOKEN_KEY, token.trim());
      setAuthed(true);
      toast.success("Welcome back boss.");
    } catch (error) {
      toast.error(errMessage(error, "Wrong token."));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch (e) {
      /* ignore */
    }
    setAuthed(false);
    setToken("");
    setItems([]);
    setSummary(null);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.adminUpdate(token, kind, id, status);
      setItems((list) => list.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success("Status updated.");
      api.adminSummary(token).then(setSummary).catch(() => {});
    } catch (error) {
      toast.error(errMessage(error));
    }
  };

  const remove = async (id) => {
    try {
      await api.adminDelete(token, kind, id);
      setItems((list) => list.filter((it) => it.id !== id));
      toast.success("Record deleted.");
      api.adminSummary(token).then(setSummary).catch(() => {});
    } catch (error) {
      toast.error(errMessage(error));
    }
  };

  if (!authed) {
    return (
      <div
        className="flex min-h-screen items-center justify-center px-4"
        style={{ background: "var(--gm-navy-950)" }}
      >
        <form
          onSubmit={login}
          className="gm-card w-full max-w-sm"
          data-testid="admin-token-login"
        >
          <img
            src="/goldmap-logo.png"
            alt="Goldmap EA"
            width={56}
            height={56}
            className="rounded-full"
            style={{ border: "1px solid rgba(255,255,255,0.25)" }}
          />
          <h1 className="mt-4 text-xl font-bold text-white">Owner dashboard</h1>
          <p className="mt-1.5 text-xs" style={{ color: "var(--gm-muted)" }}>
            Enter your admin token to see enquiries and applications.
          </p>

          <div className="mt-5">
            <Label htmlFor="admin-token" className="text-white">
              Admin token
            </Label>
            <Input
              id="admin-token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste token"
              data-testid="admin-token-input"
              className="mt-1.5"
            />
          </div>

          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-button"
            className="gm-btn-red mt-5 flex min-h-[46px] w-full items-center justify-center gap-2 rounded-xl text-sm font-bold disabled:opacity-60"
          >
            {loggingIn ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <KeyRound className="h-4 w-4" />
            )}
            {loggingIn ? "Checking..." : "Enter dashboard"}
          </button>

          <Link
            to="/"
            data-testid="admin-back-link"
            className="mt-4 inline-flex items-center gap-2 text-xs font-semibold"
            style={{ color: "var(--gm-muted)" }}
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--gm-navy-950)" }}>
      <header
        className="sticky top-0 z-30"
        style={{
          background: "rgba(7,11,29,0.9)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <div className="gm-container flex h-16 items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img
              src="/goldmap-logo.png"
              alt="Goldmap EA"
              width={34}
              height={34}
              className="rounded-full"
              style={{ border: "1px solid rgba(255,255,255,0.25)" }}
            />
            <div>
              <p className="font-display text-sm font-bold text-white">Owner dashboard</p>
              <p className="text-[10px]" style={{ color: "var(--gm-muted-2)" }}>
                Goldmap EA submissions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="gm-btn-ghost hidden min-h-[40px] items-center rounded-xl px-3 text-xs font-semibold sm:inline-flex"
            >
              View site
            </Link>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout-button"
              className="gm-btn-ghost inline-flex min-h-[40px] items-center gap-2 rounded-xl px-3 text-xs font-semibold"
            >
              <LogOut className="h-3.5 w-3.5" />
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="gm-container py-8">
        <div className="grid gap-3 sm:grid-cols-3" data-testid="admin-summary">
          {KINDS.map((k) => (
            <div
              key={k.slug}
              className="gm-card"
              data-testid={`admin-summary-${k.slug}`}
            >
              <p className="text-xs" style={{ color: "var(--gm-muted-2)" }}>
                {k.label}
              </p>
              <p className="font-display mt-1 text-2xl font-extrabold text-white">
                {summary?.[k.slug]?.total ?? "-"}
              </p>
              <p className="mt-1 text-xs" style={{ color: "var(--gm-muted)" }}>
                {summary?.[k.slug]?.new ?? 0} new
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Tabs value={kind} onValueChange={setKind}>
            <TabsList
              className="flex h-auto w-full flex-wrap justify-start gap-1 p-1"
              data-testid="admin-kind-tabs"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
              }}
            >
              {KINDS.map((k) => (
                <TabsTrigger
                  key={k.slug}
                  value={k.slug}
                  data-testid={`admin-tab-${k.slug}`}
                  className="min-h-[40px] flex-1 rounded-lg px-3 text-xs font-semibold"
                >
                  {k.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2"
                style={{ color: "var(--gm-muted-2)" }}
              />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    load();
                  }
                }}
                placeholder="Search name, email, telegram, ref code..."
                data-testid="admin-search-input"
                className="h-11 pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11 sm:w-44" data-testid="admin-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => load()}
              data-testid="admin-refresh-button"
              className="gm-btn-ghost inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          <div className="mt-5" data-testid="admin-data-list">
            {loading ? (
              <div className="space-y-3">
                {[0, 1, 2].map((i) => (
                  <Skeleton key={i} className="h-28 w-full rounded-xl" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div
                className="rounded-xl p-10 text-center"
                data-testid="admin-empty-state"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px dashed rgba(255,255,255,0.16)",
                }}
              >
                <p className="text-sm font-semibold text-white">Nothing here yet</p>
                <p className="mt-1.5 text-xs" style={{ color: "var(--gm-muted)" }}>
                  New submissions will show up in this list.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {items.map((it) => (
                  <article
                    key={it.id}
                    className="gm-card"
                    data-testid={`admin-row-${it.ref_code}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-mono-gm text-xs font-semibold" style={{ color: "var(--gm-info)" }}>
                            {it.ref_code}
                          </p>
                          <StatusBadge status={it.status} />
                        </div>
                        <p className="mt-2 text-sm font-bold text-white">{it.name}</p>
                        <p className="mt-1 text-xs" style={{ color: "var(--gm-muted)" }}>
                          {[it.telegram, it.email].filter(Boolean).join(" \u00b7 ") || "-"}
                        </p>
                      </div>
                      <p className="text-xs" style={{ color: "var(--gm-muted-2)" }}>
                        {fmtDate(it.created_at)}
                      </p>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {it.topic ? <span className="gm-chip">topic: {it.topic}</span> : null}
                      {it.plan_name ? <span className="gm-chip">{it.plan_name}</span> : null}
                      {it.plan_price ? <span className="gm-chip">{it.plan_price}</span> : null}
                      {it.account_count ? (
                        <span className="gm-chip">{it.account_count} accounts</span>
                      ) : null}
                      {it.platform ? <span className="gm-chip">{it.platform}</span> : null}
                      {it.account_id ? (
                        <span className="gm-chip">ID: {it.account_id}</span>
                      ) : null}
                      {it.account_type ? <span className="gm-chip">{it.account_type}</span> : null}
                      {kind === "free-access" ? (
                        <>
                          <span className="gm-chip">
                            link: {it.registered_via_link ? "yes" : "no"}
                          </span>
                          <span className="gm-chip">
                            verified: {it.broker_verified ? "yes" : "no"}
                          </span>
                        </>
                      ) : null}
                    </div>

                    {it.message || it.notes ? (
                      <p
                        className="mt-4 whitespace-pre-wrap rounded-xl p-3 text-sm leading-relaxed"
                        style={{
                          background: "rgba(255,255,255,0.03)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          color: "var(--gm-muted)",
                        }}
                      >
                        {it.message || it.notes}
                      </p>
                    ) : null}

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {STATUS_OPTIONS.map((s) => (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => updateStatus(it.id, s.value)}
                          disabled={it.status === s.value}
                          data-testid={`admin-set-${s.value}-${it.ref_code}`}
                          className="gm-btn-ghost min-h-[38px] rounded-lg px-3 text-xs font-semibold disabled:opacity-40"
                        >
                          {s.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => remove(it.id)}
                        data-testid={`admin-delete-${it.ref_code}`}
                        aria-label={`Delete ${it.ref_code}`}
                        className="gm-btn-ghost ml-auto inline-flex h-[38px] w-[38px] items-center justify-center rounded-lg"
                      >
                        <Trash2 className="h-3.5 w-3.5" style={{ color: "var(--gm-danger)" }} />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
